import { NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const opsTabs = [
  {
    label: "Home",
    path: "/admin/ops",
    end: true,
    title: "Ops Tooling",
    description:
      "Search all firms and users by name or email, view firm details, impersonate a user, review login logs, and (superuser) delete firms.",
  },
  {
    label: "Code Lab",
    path: "/admin/ops/codes",
    end: false,
    title: "Code Lab",
    description: "Tally a transaction code across every firm, or find start→end code sequences within N days, with drill-down to the owning transcript.",
  },
  {
    label: "CSED Audit",
    path: "/admin/ops/csed",
    end: false,
    title: "CSED Audit",
    description: "Run a CSED discrepancy audit for a firm and export the results to Excel.",
  },
  {
    label: "TDS Health",
    path: "/admin/ops/tds",
    end: false,
    title: "TDS Health",
    description: "View hourly TDS health metrics — request counts, errors, and unknowns — for a selected date.",
  },
  {
    label: "Errors",
    path: "/admin/ops/errors",
    end: false,
    title: "Errors",
    description: "View application error logs captured by the background log-check job, and trigger scans on demand.",
  },
  {
    label: "Bad A2A",
    path: "/admin/ops/bad-a2a",
    end: false,
    title: "Bad A2A",
    description: "Monitor clients with broken A2A / CAF linkage and clear the counter.",
  },
  {
    label: "Reports",
    path: "/admin/ops/reports",
    end: false,
    title: "Ops Reports",
    description: "Download operations reports by kind, with an optional date range for dated reports like e-signature.",
  },
  {
    label: "Test Users",
    path: "/admin/ops/test-users",
    end: false,
    title: "Test Users",
    description: "View all seeded demo / test accounts in the system (matched by the example domain).",
  },
  {
    label: "Jobs",
    path: "/admin/ops/jobs",
    end: false,
    title: "Background Jobs",
    description: "Manage scheduled background jobs — view cadence, last-run status, and full run history, and trigger manual runs.",
  },
] as const;

function activeTab(pathname: string) {
  // Most-specific path first so a sub-path wins over the "/admin/ops" home entry.
  return (
    [...opsTabs]
      .sort((a, b) => b.path.length - a.path.length)
      .find((t) =>
        t.end ? pathname === t.path : pathname === t.path || pathname.startsWith(`${t.path}/`),
      ) ?? opsTabs[0]
  );
}

/**
 * Shell for the staff-only ops tooling (`/admin/ops/*`). Gated upstream by
 * `PlatformAdminRoute`. A sticky top tab strip (like the Settings area) plus a
 * per-page heading + description driven by the active tab.
 */
export function OpsLayout() {
  const tab = activeTab(useLocation().pathname);
  return (
    <div className="flex flex-col gap-6">
      <div className="bg-card -mx-5 -mt-6 min-w-0 border-b px-5 py-2.5 md:-mx-10 md:px-10 md:sticky md:top-0 md:z-10">
        <div className="flex flex-wrap items-center gap-1">
          {opsTabs.map((t) => (
            <NavLink
              key={t.path}
              to={t.path}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "flex h-9 shrink-0 items-center rounded-[10px] px-3.5 text-[13px] font-medium transition-colors",
                  isActive
                    ? "bg-brand-panel font-semibold text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted",
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">{tab.title}</h1>
        <p className="text-muted-foreground text-sm">{tab.description}</p>
      </div>
      <div>
        <Outlet />
      </div>
    </div>
  );
}
